import {
  GroupedBotProperty,
  PropertyFieldType
} from '../../../../../../../../../BotProperties/components/Properties/types'
import { ActionNodeProps } from '../../types'
import { ActionNodeActionProxy } from '../index'
import { PropertyAction } from './PropertyAction'
import {
  PropValueCalculate,
  PropValueConstant,
  PropValueProperty,
} from './PropertyActionValue'
import { DATE, DATE_TIME, NUMBER, STRING, TIME, } from './VariableCompatibility'
import { GetPropertyActionValueSample } from './helper'
import { PropertyActionType, SetPropertyActionValue } from './types'

export class PropertyActionProxy extends ActionNodeActionProxy {
  action: PropertyAction
  private nodeProps: ActionNodeProps

  constructor(action: PropertyAction, nodeProps: ActionNodeProps) {
    super()
    this.nodeProps = nodeProps

    this.action = action
  }

  isActionValid(): boolean {
    if (this.action.value.type === null) {
      return false
    } else if (this.action.value instanceof PropValueProperty) {
      return this.action.value.property_index !== null
    } else {
      return true
    }
  }

  getModel(): PropertyAction {
    return this.action
  }

  get currentProperty(): GroupedBotProperty | undefined {
    return this.nodeProps.getProperties().find((p) => p.id === this.action.property_index)
  }

  get propertyActionValueTypes(): Array<SetPropertyActionValue> {
    if (this.currentProperty) {
      switch (this.currentProperty.type) {
        case PropertyFieldType.Date:
        case PropertyFieldType.DateTime:
        case PropertyFieldType.Time:
          return [
            SetPropertyActionValue.Property,
            SetPropertyActionValue.Constant,
            SetPropertyActionValue.Now,
          ]
        case PropertyFieldType.Number:
        case PropertyFieldType.Double:
          return [
            SetPropertyActionValue.Property,
            SetPropertyActionValue.Constant,
            SetPropertyActionValue.Calculate,
          ]
        default:
          return [
            SetPropertyActionValue.Property,
            SetPropertyActionValue.Constant,
          ]
      }
    }

    return []
  }

  get compatibleProperties(): Array<GroupedBotProperty> {
    const properties = this.nodeProps.getProperties()
    const property = this.currentProperty

    if (property) {
      const checkProp = (fieldTypesArr: Array<PropertyFieldType>, prop: GroupedBotProperty) => {
        if (fieldTypesArr.includes(prop.type) && prop.id !== property.id) {
          return prop
        }
      }

      switch (property.type) {
        case PropertyFieldType.String:
        case PropertyFieldType.Email:
        case PropertyFieldType.Phone:
          return properties.filter((prop) => checkProp(STRING, prop))
        case PropertyFieldType.Double:
        case PropertyFieldType.Number:
          return properties.filter((prop) => checkProp(NUMBER, prop))
        case PropertyFieldType.DateTime:
          return properties.filter((prop) => checkProp(DATE_TIME, prop))
        case PropertyFieldType.Time:
          return properties.filter((prop) => checkProp(TIME, prop))
        case PropertyFieldType.Date:
          return properties.filter((prop) => checkProp(DATE, prop))
        case PropertyFieldType.Flag:
          return properties.filter((prop) => checkProp([ PropertyFieldType.Flag ], prop))
        case PropertyFieldType.Files:
          return properties.filter((prop) => checkProp([ PropertyFieldType.Files ], prop))
      }
    }
    return []
  }

  get availableActionTypes(): Array<PropertyActionType> {
    if (this.currentProperty) {
      const fullAction = [
        PropertyFieldType.Number,
        PropertyFieldType.Double,
        PropertyFieldType.Date,
        PropertyFieldType.Time,
        PropertyFieldType.DateTime
      ]

      if (fullAction.includes(this.currentProperty.type)) {
        return Object.values(PropertyActionType)
      } else {
        return [ PropertyActionType.Set ]
      }
    }

    return []
  }

  setPropertyIndex(index: number): void {
    this.action.property_index = index
    this.normalizeAction()
  }

  setActionType(type: PropertyActionType): void {
    this.action.action = type
    this.normalizeAction()
  }

  setValueType(type: SetPropertyActionValue): void {
    if (this.currentProperty) {
      this.action.value = GetPropertyActionValueSample(type, this.currentProperty?.type)
    }
    this.normalizeAction()
  }

  setValuePropsIndex(index: number): void {
    if (this.action.value instanceof PropValueProperty) {
      this.action.value.property_index = index
    }
    this.normalizeAction()
  }

  setValueInActionValue(value: string | number | boolean): void {
    if (this.action.value instanceof PropValueCalculate && typeof value === 'string') {
      this.action.value.value = value
    }

    if (this.action.value instanceof PropValueConstant) {
      this.action.value.value = value
    }
  }

  normalizeAction(): void {
    if (this.availableActionTypes.length && !this.availableActionTypes.includes(this.action.action)) {
      this.action.action = this.availableActionTypes[0]
    }

    const valueTypes = this.propertyActionValueTypes

    if (valueTypes.length && this.action.value.type && !valueTypes.includes(this.action.value.type)) {
      const firstType = valueTypes[0]

      if (this.currentProperty) {
        this.action.value = GetPropertyActionValueSample(firstType, this.currentProperty.type)
      }
    }

    this.normalizeValue()
  }

  normalizeValue(): void {
    if (this.action.value instanceof PropValueProperty) {
      this.normalizePropertyValue(this.action.value)
    }

    if (this.action.value instanceof PropValueConstant) {
      this.normalizeConstantValue(this.action.value)
    }
  }

  normalizePropertyValue(value: PropValueProperty): void {
    const id = value.property_index
    const valueProp = this.nodeProps.getProperties().find((p) => p.id === id)
    const compatiblePropTypes = this.compatibleProperties.map((p) => p.type)

    if (valueProp && !compatiblePropTypes.includes(valueProp.type)) {
      value.property_index = null
    }
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  normalizeConstantValue(value: PropValueConstant): void {
    if (this.currentProperty) {
      if ([ PropertyFieldType.Double, PropertyFieldType.Number ].includes(this.currentProperty.type)) {
        value.value = typeof value.value === 'number' ? value.value : 0
      }

      if (![ PropertyFieldType.Double, PropertyFieldType.Number ].includes(this.currentProperty.type)) {
        value.value = typeof value.value === 'string' ? value.value : ''
      }

      if ([ PropertyFieldType.DateTime, PropertyFieldType.Date, PropertyFieldType.Time ].includes(this.currentProperty.type)) {
        // eslint-disable-next-line sonarjs/no-all-duplicated-branches
        value.value = typeof value.value === 'string' ? 0 : typeof value.value === 'number' ? value.value : 0
      }

      if (this.currentProperty.type === PropertyFieldType.Files) {
        value.value = JSON.stringify([])
      }
    }
  }
}
